interface Options {
  saveDelay?: number
  addUndoDelay?: number
  saveImpl?: () => void
}

export const useUndo = (options?: Options) => {

  const old: Ref<string> = ref(null)
  const maxSize: number = 10
  const saveDelay: number = options?.saveDelay ? options.saveDelay : 750
  const addUndoDelay: number = options?.addUndoDelay ? options.addUndoDelay : 1500
  const _saveImpl: () => void = options?.saveImpl
    ? options?.saveImpl
    : () => { console.warn('implement auto save') }

  let _autosaveEnabled: Ref<boolean> = ref(false)
  const autosaveEnabled = computed({
    get: () => _autosaveEnabled.value,
    set: (v) => _autosaveEnabled.value = v,
  })

  let debouncedSave = null
  let debouncedAddUndo = null

  let started: boolean = false

  const stack: Ref<string[]> = ref([])
  const index: Ref<number> = ref(0)

  const _undoable: Ref<any> = ref()
  const undoable = computed({
    get: () => _undoable.value,
    set: (v) => _undoable.value = v,
  })

  let _waitSaving: Ref<boolean> = ref(false)
  const waitSaving = computed({
    get: () => _waitSaving.value,
    set: (v) => _waitSaving.value = v,
  })

  let _ignoreChange: Ref<boolean> = ref(false)
  const ignoreChange = computed({
    get: () => _ignoreChange.value,
    set: (v) => _ignoreChange.value = v,
  })

  const saveImpl = async () => {
    _saveImpl()
  }

  const save = async () => {
    try {
      await saveImpl()
      old.value = JSON.stringify(undoable.value, jsonReplacer)
    } catch (e) {
      console.info(e)
    } finally {
      waitSaving.value = false
    }
  }

  const forceSave = async () => {
    if (debouncedSave) debouncedSave.cancel()
    await save()
    debouncedSave = debounce(saveDelay, save)
  }

  const watchUndoable = () => {
    // console.info('watch undoable', this.started, this.ignoreChange)
    if (!started) return
    if (!ignoreChange.value) {
      debouncedAddUndo()
      waitSaving.value = true
      if (autosaveEnabled.value) debouncedSave()
    } else {
      ignoreChange.value = false
    }
  }
  watch(
    () => undoable,
    () => {
      watchUndoable()
    },
    { deep: true },
  )

  const canUndo = computed(() => {
    return index.value > 0
  })
  const canRedo = computed(() => {
    return stack.value.length - 1 > index.value
  })
  const canSave = computed(() => {
    return JSON.stringify(undoable.value, jsonReplacer) !== old.value
  })

  const update = (obj: any) => {
    undoable.value = obj
    waitSaving.value = index.value > 0
    if (autosaveEnabled.value) debouncedSave()
  }

  const redo = () => {
    if (canRedo.value) {
      index.value++
      update(JSON.parse(stack.value[index.value]))
    }
  }

  const undo = () => {
    if (canUndo.value) {
      index.value--
      update(JSON.parse(stack.value[index.value]))
    }
  }

  const snapshot = () => {
    addUndo()
  }

  const resetHistory = () => {
    stack.value = [JSON.stringify(undoable.value)]
    index.value = 0
    old.value = JSON.stringify(undoable.value, jsonReplacer)
    started = true
  }

  const addUndo = () => {
    let json = JSON.stringify(undoable.value)
    let jsonSimple = JSON.stringify(undoable.value, jsonReplacer)
    let stackJsonSimple = JSON.stringify(JSON.parse(stack.value[index.value]), jsonReplacer)
    if (jsonSimple !== stackJsonSimple) {
      //Redo領域の削除
      if (stack.value.length > index.value) {
        stack.value.splice(index.value + 1)
      }
      stack.value.push(json)
      index.value++
      //最大サイズを超えたら古い履歴を削除
      if (index.value > maxSize) {
        stack.value.splice(0, 1)
        index.value--
      }
    }
  }

  debouncedSave = debounce(saveDelay, save)
  debouncedAddUndo = debounce(addUndoDelay, addUndo)

  return {
    autosaveEnabled,
    undoable,
    waitSaving,
    ignoreChange,
    saveImpl,
    save,
    forceSave,
    watchUndoable,
    canUndo,
    canRedo,
    canSave,
    redo,
    undo,
    snapshot,
    resetHistory,
  }
}
